import { JSX, forwardRef, FormEvent, ReactNode, useCallback, useImperativeHandle, useRef } from 'react'

import { isTRPCClientError } from '@/utils/trpc'
import { ValidationErrors } from '@/utils/misc'

export type FormStatus = 'idle' | 'submitting' | 'submitted' | 'error'

export type FormSubmitResult = {
    error?: string | boolean
    validation?: ValidationErrors
    successMessage?: string
    redirected?: boolean
    canceled?: boolean
    duration?: number
}

export type FormState = {
    status: FormStatus
    result: FormSubmitResult | null
}

export type FormReferenceHandle = {
    getStatus: () => FormStatus
    getResult: () => FormSubmitResult | null
    submit: (action?: string, args?: any) => Promise<boolean | any>
}

type FormSubmitEventHandler = (action: string, args?: any) => Promise<FormSubmitResult | boolean | any | void>
type FormStateEventHandler = (state: FormState) => void

export type FormProps = {
    className?: string
    disabled?: boolean
    status: FormStatus
    onState: FormStateEventHandler
    result: FormSubmitResult | null
    onSubmit: FormSubmitEventHandler | null
    children: ReactNode
    allowSubmitOnEnter?: boolean
}

export const Form = forwardRef<FormReferenceHandle, FormProps>(
    (
        { className, disabled, status, result, onSubmit, children, allowSubmitOnEnter, onState }: FormProps,
        ref
    ): JSX.Element => {
        const formRef = useRef<HTMLFormElement>(null)
        const isSubmittingRef = useRef(false)
        const latestResultRef = useRef(result)
        if (!isSubmittingRef.current) {
            latestResultRef.current = result
        }

        const doSubmit = useCallback(
            async (action: string = '', args: any = undefined) => {
                if (!onSubmit) {
                    return
                }

                if (isSubmittingRef.current) {
                    return false
                }

                isSubmittingRef.current = true
                latestResultRef.current = null
                onState({
                    status: 'submitting',
                    result: null,
                })

                let newStatus: FormStatus = 'error'
                let newResult: FormSubmitResult | null = null

                let result: any = false

                try {
                    const submitResult = await onSubmit(action, args)

                    if (submitResult === undefined) {
                        newStatus = 'submitted'
                        result = true
                    } else if (typeof submitResult === 'boolean') {
                        newStatus = submitResult ? 'submitted' : 'error'
                        result = newStatus === 'submitted'
                    } else if (submitResult?.redirected || submitResult?.canceled) {
                        // nothing to do here just go back to idle
                        newStatus = 'idle'
                    } else if (
                        submitResult?.error ||
                        submitResult?.validation ||
                        typeof submitResult?.successMessage === 'string'
                    ) {
                        newResult = submitResult as FormSubmitResult
                        newStatus = submitResult.error ? 'error' : 'submitted'
                        result = newStatus === 'submitted'
                    } else {
                        newStatus = 'submitted'
                        result = submitResult
                    }
                } catch (e) {
                    result = false
                    newStatus = 'error'

                    if (isTRPCClientError(e)) {
                        if (e.data?.validation) {
                            newResult = { error: e.message, validation: new ValidationErrors(e.data.validation) }
                        } else {
                            newResult = { error: e.message || true }
                        }
                    } else {
                        console.error(e)
                        newResult = { error: true }
                    }
                }

                latestResultRef.current = newResult
                onState({
                    status: newStatus,
                    result: newResult,
                })
                isSubmittingRef.current = false

                return result
            },
            [onState, onSubmit]
        )

        const handleSubmit = useCallback(
            (evt: FormEvent<HTMLFormElement>) => {
                if (allowSubmitOnEnter === true) {
                    setTimeout(doSubmit, 0)
                }

                evt.preventDefault()
                return false
            },
            [doSubmit, allowSubmitOnEnter]
        )

        useImperativeHandle(ref, () => ({
            getStatus: () => status,
            getResult: () => latestResultRef.current,
            submit: async (action: string = '', args?: any) => {
                if (formRef.current) {
                    if (!formRef.current.checkValidity()) {
                        // simulate submit which should cause the form to fail and not submit but render errors
                        formRef.current.requestSubmit()
                        return false
                    }

                    const result = await doSubmit(action, args)

                    // wait for the next tick to show the toast result
                    await new Promise((resolve) => setTimeout(resolve, 0))

                    return result
                }

                return false
            },
        }))

        return (
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className={className}
                // @ts-expect-error react not supporting this yet
                inert={disabled || status === 'submitting' ? '' : undefined}
            >
                {children}
            </form>
        )
    }
)
